import clsx from 'clsx'

import { useMemo, useState, useEffect } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'

import { ResultImage } from 'libs/searchspring/components/ResultImage'
import { Price } from 'libs/searchspring/components/Price'
import { useBadges } from '@/components/hooks/useBadges'
import { Badge } from 'libs/searchspring/components/Badge'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { usePromolines } from '@/components/hooks/usePromolines'
import { useSwatches } from 'libs/searchspring/hooks/useSwatches'
import { ResultsSwatches } from 'libs/searchspring/components/ResultSwatches'
import { useGuides } from '@/components/hooks/useGuides'
import { useRanges } from '@/components/hooks/useRanges'
import { ResultQuickAdd, isGiftCard } from 'libs/searchspring/components/ResultQuickAdd'
import { hasB2BDiscount } from '@/components/custom-elements/b2b-price'

const DEFAULT_OPTIONS = {
  hideQuickAdd: false,
  hideSwatches: false,
  hidePromolines: false,
  hideBadges: false,
  hideWishlist: false,
  hideRange: false
}

export function ResultsCard({ result, className, options = DEFAULT_OPTIONS, dataIndex = '0' }) {
  const [variant] = result.variants
  const isB2BCustomer = window.globals?.customer?.b2b
  const b2bPricing = window.globals?.customer?.b2bPricing
  const productId = result.uid || result.id?.toString?.() || result.id
  const b2bData = isB2BCustomer && b2bPricing ? b2bPricing[productId] : null
  const hasQuickB2bCheck = isB2BCustomer && b2bData && b2bData.price
  const [b2bPriceCheck, setB2bPriceCheck] = useState(hasQuickB2bCheck)

  const {
    hideQuickAdd = DEFAULT_OPTIONS.hideQuickAdd,
    hideSwatches = DEFAULT_OPTIONS.hideSwatches,
    hidePromolines = DEFAULT_OPTIONS.hidePromolines,
    hideBadges = DEFAULT_OPTIONS.hideBadges,
    hideWishlist = DEFAULT_OPTIONS.hideWishlist,
    hideRange = DEFAULT_OPTIONS.hideRange
  } = options
  const { currentResult, updateSwatch } = useSwatches(result)
  const clickLogger = useBeaconLogClick()

  const currentVariant = currentResult?.variants?.[0] || variant

  // For non-B2B customers, use regular pricing
  const finalPrice = currentVariant.price
  const finalCompareAtPrice = currentVariant.compare_at_price

  useEffect(() => {
    if (isB2BCustomer && currentVariant?.id) {
      hasB2BDiscount(currentVariant.id).then(hasDiscount => {
        setB2bPriceCheck(hasDiscount)
      }).catch(() => {
        setB2bPriceCheck(false)
      })
    } else {
      setB2bPriceCheck(false)
    }
  }, [isB2BCustomer, currentVariant?.id])

  const criteria = useMemo(() => ({
    tags: currentResult?.tags ?? [],
    collections: currentResult?.collection_handle ?? [],
    products: [currentResult.handle]
  }), [currentResult?.tags, currentResult?.collection_handle, currentResult?.handle])

  const allBadges = useBadges(criteria)
  const allPromolines = usePromolines(criteria)
  const [sizeGuide] = useGuides(criteria)
  const [rangeInfo] = useRanges(criteria)

  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstBadge, secondBadge] = useMemo(() => getObjects(allBadges), [allBadges])
  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  const filteredSwatches = useMemo(() =>
    currentResult.swatches?.filter(swatch =>
      !swatch.tags?.includes('Flow:OutOfStock')
    ) || []
  , [currentResult.swatches])

  const url = useMemo(() => {
    return `/products/${currentResult.handle}`
  }, [currentResult])

  const handleLogClick = () => {
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(currentResult)
    clickLogger(currentResult)
  }

  return (
    <div className={clsx('group/card relative flex', className)} id={`result__${currentResult.handle}`} data-dl-track="select_item" data-dl-product-id={currentResult.uid} data-dl-index={dataIndex}>
      <div className="grow grid grid-cols-1 w-full aspect-4/5 children:col-[1/1] children:row-[1/2] outline-card">
        <a href={url} onClick={handleLogClick} aria-label={currentResult.name}>
          <ResultImage result={currentResult}></ResultImage>
        </a>

        <div className="flex justify-between gap-x-2 absolute inset-tl w-full h-fit p-1.5 z-2">
          <div className="flex flex-col">
            <a href={url} className="paragraph-b lg:paragraph-m font-semibold uppercase link-fade" aria-label={currentResult.name} dangerouslySetInnerHTML={{ __html: currentResult.mfield_custom_web_friendly_title || currentResult.name }}></a>
            {isB2BCustomer ? (
              <b2b-price
                class="group/price flex gap-x-0.25"
                data-variant-id={currentVariant.id}>
                <span data-original-price class="hidden group-data-[sale=true]/price:block line-through paragraph-b"></span>
                <span data-b2b-price class="group-data-[sale=true]/price:text-brand-red paragraph-b"></span>
              </b2b-price>
            ) : (
              <Price price={finalPrice} compareAtPrice={finalCompareAtPrice} containerClass="flex gap-x-0.25" priceClass="paragraph-b" />
            )}
          </div>
          {!hideRange && rangeInfo && rangeInfo.image && (
            <span className="flex size-fit max-w-[52px] lg:max-w-[68px] children:size-auto children:size-contain">
              {rangeInfo.cta_url ? (
                <a href={rangeInfo.cta_url} className="link-fade" aria-label={rangeInfo.display_name || 'Range Image'}>
                  <img src={rangeInfo.image} alt={rangeInfo.display_name || 'Range Image'} />
                </a>
              ) : (
                <img src={rangeInfo.image} alt={rangeInfo.display_name || 'Range Image'} />
              )}
            </span>
          )}
        </div>

        <div className="flex lg:grid lg:grid-cols-1 lg:auto-rows-min lg:children:col-[1/1] lg:children:row-[1/2] items-end lg-max:justify-between absolute inset-bl w-full h-fit p-1.5 z-2 overflow-hidden">
          <div className="grow flex flex-wrap items-center gap-0.5 gap-y-0.125 w-full group-hover/card:lg:opacity-0 group-hover/card:lg:invisible group-hover/card:lg:pointer-events-none group-hover/card:lg:translate-y-sm transition-all">
            {!hideBadges && (firstBadge || secondBadge) && (
              <>
                {firstBadge && <Badge badge={firstBadge} badgeLocation="PLP" product={currentResult} />}
                {secondBadge && <Badge badge={secondBadge} badgeLocation="PLP" product={currentResult} />}
              </>
            )}
            {!hidePromolines && (b2bPriceCheck || firstPromoline || secondPromoline) && (
              <>
                {b2bPriceCheck && (
                  <Promoline promoline={{
                    title: 'Wholesale Price',
                    text_colour: '#9C0000',
                    background_colour: '#FCEBEB'
                  }} promolineSize="medium" />
                )}
                {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="PLP" promolineSize="medium" />}
                {!b2bPriceCheck && secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="PLP" promolineSize="medium" />}
              </>
            )}
            {filteredSwatches.length > 1 && (
              <span className="shrink-0 hidden lg:flex items-center gap-x-0.5 size-fit px-0.5 py-0.375 text-brand-black bg-brand-white rounded-80 overflow-hidden">
                <span className="text-xs leading-none font-semibold uppercase">{filteredSwatches.length} Colours</span>
              </span>
            )}
          </div>
          <div className="shrink-0 flex gap-x-1 items-end justify-between lg:opacity-0 group-hover/card:lg:opacity-100 lg:invisible group-hover/card:lg:visible lg:pointer-events-none group-hover/card:lg:pointer-events-auto lg:translate-y-sm group-hover/card:lg:translate-y-0 transition-all z-2">
            {!hideSwatches && <ResultsSwatches result={currentResult} swatches={result.swatches} onSelectSwatch={updateSwatch} />}
            <div className="grow flex items-center justify-end gap-x-0.5 w-fit">
              {!hideWishlist && (
                <div className="wishlist-button flex">
                  <button type="button"
                    data-product-handle={currentResult.handle}
                    aria-label="Add to Wishlist"
                    className="wkh-button save-wishlist btn btn-secondary btn-fill btn-square w-[24px] lg:w-[36px]"
                  >
                    <div className="w-[15px] h-[14px] children:size-full">
                      <svg className="wkh-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                        <path d="M16.3407 10.8381C17.7072 9.49914 19.092 7.89421 19.092 5.79405C19.092 4.45629 18.5606 3.17331 17.6146 2.22737C16.6687 1.28143 15.3857 0.75 14.048 0.75C12.4339 0.75 11.2967 1.20855 9.92101 2.5842C8.54536 1.20855 7.40815 0.75 5.79405 0.75C4.45629 0.75 3.17331 1.28143 2.22737 2.22737C1.28143 3.17331 0.75 4.45629 0.75 5.79405C0.75 7.90339 2.12565 9.50831 3.5013 10.8381L9.92101 17.2578L16.3407 10.8381Z" fill="var(--icon-fill)" stroke="context-stroke" stroke-width="1.5px" stroke-linecap="round"/>
                      </svg>
                    </div>
                  </button>
                </div>
              )}
              {(!hideQuickAdd) && !isGiftCard(result) && !isGiftCard(currentResult) && (
                <div className="flex">
                  <ResultQuickAdd result={currentResult} firstPromoline={firstPromoline} secondPromoline={secondPromoline} sizeGuide={sizeGuide} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
